// Session-replay privacy for eBay buyer data. Sentry Replay runs on production with
// `maskAllText: false` (the rest of the app stays readable). LogRocket records the same
// sessions and redacts elements that carry `data-private`. Both tools therefore key off
// the same DOM: `sentry-mask` / `sentry-block` for Sentry, `data-private` for LogRocket.

export const REPLAY_MASK_CLASS = 'sentry-mask';
export const REPLAY_BLOCK_CLASS = 'sentry-block';

// Passed to `Sentry.replayIntegration`. `mask` is merged with Sentry's own defaults
// (`.sentry-mask`, `[data-sentry-mask]`). `block` is merged with `.sentry-block` and
// `iframe[srcdoc]` — description previews also add `sentry-block` themselves, because
// text masking does not scrub attributes while `maskAllText` is false, and a listing
// description's HTML lives in an iframe `srcdoc`.
export const SENTRY_REPLAY_OPTIONS = {
  maskAllText: false,
  blockAllMedia: false,
  mask: [`.${REPLAY_MASK_CLASS}`, '[data-private]'],
  block: [`.${REPLAY_BLOCK_CLASS}`],
};

// Request/response bodies that carry credentials: /auth/* (password, OTP, tokens) and every
// provider's /oauth/* (start answers carry the OAuth state, claim bodies a one-time claim token).
// Telegram link-token responses also carry a one-time account-linking token in the deep link.
export const hasSensitiveAuthBody = (url) =>
  typeof url === 'string' && (
    url.includes('/auth/')
    || url.includes('/oauth/')
    || /\/telegram\/link-token(?:[/?#]|$)/.test(url)
  );

const urlParts = (url) => {
  if (typeof url !== 'string' || !url) return { pathname: '', tableName: null };
  try {
    const parsed = new URL(url, 'https://wiseppc.local');
    return { pathname: parsed.pathname, tableName: parsed.searchParams.get('table_name') };
  } catch {
    return { pathname: url.split('?')[0], tableName: null };
  }
};

// olap buyer-message reads and the ETL reply routes (src/api/ebayMessages.js).
const isEbayMessagesPath = (pathname) =>
  /(?:^|\/)api\/analytics\/ebay\/messages(?:\/|$)/.test(pathname)
  || /(?:^|\/)ebay-messages(?:\/|$)/.test(pathname);

// No dedicated buyer-PII route exists today: order buyer fields are joined into the
// `ebay-orders` / `ebay-order-line-items` datasets. Match a buyers path anyway so a
// later endpoint is dropped without another pass through this file.
const isEbayBuyerPath = (pathname) =>
  /(?:^|\/)ebay\/(?:buyers|order-buyers|order_buyers)(?:\/|$)/.test(pathname);

// The dataset name is NOT a body field. `getOlapData` (src/stores/analytics.js) sends
// it as the `table_name` query parameter on POST /api/analytics/query. The response
// sanitizer sees that same URL (LogRocket copies `xhr.url` onto the response).
const isEbayAnalyticsQuery = (pathname, tableName) =>
  /(?:^|\/)api\/analytics\/query$/.test(pathname)
  && typeof tableName === 'string'
  && tableName.startsWith('ebay');

export const dropsEbayReplayBody = (url) => {
  const { pathname, tableName } = urlParts(url);
  return isEbayMessagesPath(pathname) || isEbayBuyerPath(pathname) || isEbayAnalyticsQuery(pathname, tableName);
};

// Shared by LogRocket's requestSanitizer and responseSanitizer. Drops the body only;
// headers and status stay so a failed call is still diagnosable.
export const sanitizeLogRocketNetwork = (data) => {
  if (!data || typeof data !== 'object') return data;
  if (data.headers) {
    for (const key of Object.keys(data.headers)) {
      if (key.toLowerCase() === 'authorization') data.headers[key] = '[REDACTED]';
    }
  }
  const url = typeof data.url === 'string' ? data.url : '';
  if (hasSensitiveAuthBody(url)) data.body = '[REDACTED]';
  if (dropsEbayReplayBody(url)) data.body = null;
  return data;
};
